import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { clientsApi } from "@/lib/api";

export default function ClientImport() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  // Dedupe is ON by default — re-importing a file with the same clients will
  // only add the new ones. The user can disable it to force-insert (e.g.
  // after a deliberate dedupe-key change in the source file).
  const [dedupe, setDedupe] = useState(true);

  const buildFormData = (f) => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("dedupe", dedupe ? "true" : "false");
    return fd;
  };

  const handleFile = async (f) => {
    setFile(f);
    setPreview(null);
    if (!f) return;
    setLoading(true);
    try {
      const result = await clientsApi.importPreview(buildFormData(f));
      setPreview(result);
    } catch (err) {
      toast.error("Erreur de lecture", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Re-run the preview whenever the user toggles dedupe so the counters
  // reflect the new mode without forcing them to re-pick the file.
  const handleDedupeToggle = async (next) => {
    setDedupe(next);
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dedupe", next ? "true" : "false");
      const result = await clientsApi.importPreview(fd);
      setPreview(result);
    } catch (err) {
      toast.error("Erreur de relecture", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!file) return;
    setRunning(true);
    try {
      const result = await clientsApi.importRun(buildFormData(file));
      const parts = [`${result.imported || 0} client(s) ajouté(s)`];
      if (result.duplicates) parts.push(`${result.duplicates} doublon(s) ignoré(s)`);
      if (result.skipped) parts.push(`${result.skipped} ligne(s) invalide(s)`);
      toast.success("Import terminé", { description: parts.join(" · ") });
      navigate("/clients");
    } catch (err) {
      toast.error("Erreur d'import", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="client-import-page">
      <PageHeader
        title="Importer des clients"
        subtitle="CSV ou Excel (.xlsx, .xls)"
        backTo="/clients"
      />

      <div className="px-4 md:px-8 py-6 max-w-3xl space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
          <strong>Astuce :</strong> Les en-têtes sont reconnus automatiquement (insensibles à la casse et aux accents). Ex : "Nom", "Téléphone", "Email", "Code postal", etc.
        </div>

        {/* Dedupe toggle — defaults to ON. Re-importing the same file will
            only ADD the new clients to the existing base. */}
        <label
          data-testid="dedupe-toggle"
          className="flex items-start gap-3 bg-white border border-[var(--vidoc-border)] rounded-xl p-4 cursor-pointer hover:bg-[var(--vidoc-bg-secondary)]/40 transition-colors"
        >
          <input
            type="checkbox"
            checked={dedupe}
            onChange={(e) => handleDedupeToggle(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[var(--vidoc-primary)]"
          />
          <div className="flex-1">
            <div className="font-medium flex items-center gap-2">
              <Copy className="w-4 h-4 text-[var(--vidoc-primary)]" />
              Ignorer les doublons
            </div>
            <div className="text-xs text-[var(--vidoc-text-secondary)] mt-0.5">
              Comparaison sur <strong>nom + prénom + adresse + ville</strong>{" "}
              (insensible aux accents et à la casse). Recommandé pour ajouter
              de nouveaux clients à votre base existante sans créer de
              doublons.
            </div>
          </div>
        </label>

        {/* Step 1 : choose file */}
        <div
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-[var(--vidoc-border)] rounded-2xl p-8 text-center cursor-pointer hover:border-[var(--vidoc-primary)] hover:bg-[var(--vidoc-bg-secondary)] transition-colors"
        >
          <FileSpreadsheet className="w-12 h-12 mx-auto text-[var(--vidoc-text-tertiary)] mb-3" />
          <div className="font-medium">
            {file ? file.name : "Cliquez pour sélectionner un fichier"}
          </div>
          <div className="text-sm text-[var(--vidoc-text-secondary)] mt-1">
            CSV, XLSX, XLS
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            data-testid="import-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>

        {loading && (
          <div className="text-center py-6 text-[var(--vidoc-text-secondary)] flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Analyse du fichier...
          </div>
        )}

        {/* Step 2 : preview + mapping */}
        {preview && (
          <div className="space-y-4 animate-slideUp">
            <div className="grid grid-cols-4 gap-3">
              <Stat label="Lignes" value={preview.total_rows} />
              <Stat
                label={dedupe ? "À ajouter" : "À importer"}
                value={
                  (preview.total_rows || 0) -
                  (preview.duplicates || 0) -
                  (preview.skipped || 0)
                }
                variant="success"
              />
              <Stat
                label="Doublons"
                value={preview.duplicates || 0}
                variant={preview.duplicates ? "warning" : "default"}
              />
              <Stat
                label="Invalides"
                value={preview.skipped || 0}
                variant={preview.skipped ? "warning" : "default"}
              />
            </div>

            {preview.duplicates > 0 && preview.duplicate_examples?.length > 0 && (
              <div
                data-testid="duplicate-examples"
                className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm space-y-2"
              >
                <div className="flex items-center gap-2 font-medium text-amber-900">
                  <Copy className="w-4 h-4" />
                  {preview.duplicates} doublon{preview.duplicates > 1 ? "s" : ""} détecté
                  {preview.duplicates > 1 ? "s" : ""}
                  {dedupe ? " — ils seront ignorés" : " (dédupe désactivée → ils seront importés)"}
                </div>
                <ul className="text-xs text-amber-900/80 list-disc list-inside space-y-0.5">
                  {preview.duplicate_examples.map((d) => (
                    <li key={`dup-${d.row}`}>
                      Ligne {d.row} — <strong>{[d.nom, d.prenom].filter(Boolean).join(" ")}</strong>
                      {d.ville ? ` · ${d.ville}` : ""}
                    </li>
                  ))}
                  {preview.duplicates > preview.duplicate_examples.length && (
                    <li>... et {preview.duplicates - preview.duplicate_examples.length} autre(s)</li>
                  )}
                </ul>
              </div>
            )}

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Colonnes détectées</h3>
              <div className="flex flex-wrap gap-2">
                {/* Backend returns mapped_headers as a dict {original: field}.
                    Object.entries gives us [[original, field], ...]. */}
                {Object.entries(preview.mapped_headers || {}).map(([original, field]) => (
                  <span
                    key={original}
                    data-testid={`mapped-${field}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    {original} → {field}
                  </span>
                ))}
                {preview.unmapped_headers?.map((h) => (
                  <span
                    key={h}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-medium"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    {h}
                  </span>
                ))}
              </div>
            </div>

            {preview.preview?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">Aperçu (5 premières lignes)</h3>
                <div className="overflow-x-auto bg-white border border-[var(--vidoc-border)] rounded-xl">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--vidoc-bg-secondary)]">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wide text-[var(--vidoc-text-secondary)]">
                          Ligne
                        </th>
                        {Object.keys(preview.preview[0].data || {}).map((k) => (
                          <th
                            key={k}
                            className="text-left px-3 py-2 font-medium text-xs uppercase tracking-wide text-[var(--vidoc-text-secondary)]"
                          >
                            {k}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.slice(0, 5).map((row) => {
                        const dataEntries = Object.entries(row.data || {});
                        return (
                          <tr
                            key={`preview-row-${row.row ?? dataEntries.map(([k, v]) => `${k}:${v}`).join("|")}`}
                            className="border-t border-[var(--vidoc-border)]"
                          >
                            <td className="px-3 py-2 text-xs text-[var(--vidoc-text-secondary)]">
                              {row.row ?? ""}
                            </td>
                            {dataEntries.map(([colName, v]) => (
                              <td
                                key={colName}
                                className="px-3 py-2 truncate max-w-[140px]"
                              >
                                {String(v ?? "")}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Effective count = total - duplicates (if dedupe) - skipped */}
            <button
              data-testid="import-confirm-btn"
              onClick={handleConfirm}
              disabled={running}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[var(--vidoc-primary)] text-white font-semibold touch-target disabled:opacity-50 hover:bg-[var(--vidoc-primary-dark)]"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {(() => {
                const toAdd =
                  (preview.total_rows || 0) -
                  (dedupe ? preview.duplicates || 0 : 0) -
                  (preview.skipped || 0);
                return `Ajouter ${toAdd} client${toAdd > 1 ? "s" : ""} à la base`;
              })()}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, variant }) {
  const color =
    variant === "success"
      ? "text-[var(--vidoc-success)]"
      : variant === "warning"
      ? "text-[var(--vidoc-warning)]"
      : "text-[var(--vidoc-text)]";
  return (
    <div className="bg-white border border-[var(--vidoc-border)] rounded-xl p-3">
      <div className="text-xs text-[var(--vidoc-text-secondary)]">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
